import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import TwilioError from '@twilio/voice-sdk/es5/twilio/errors/twilioError';
import { requestTwilioConfig } from '../asyncHelpers/configService';
import { SerializedTwilioCall, SerializedTwilioDevice } from '../utils/twilioHelpers';
import { createParametricSelectorHook } from './hooks';
import { RootState } from './store';

export interface DialerState {
    isBootstrapped: boolean;
    twilioDevice: SerializedTwilioDevice | undefined;
    twilioCall: SerializedTwilioCall | undefined;
    callStart: string | undefined;
    sendDigits: string[];
    twilioError: TwilioError | undefined;
}

const initialState: DialerState = {
    isBootstrapped: false,
    twilioDevice: undefined,
    twilioCall: undefined,
    callStart: undefined,
    sendDigits: [],
    twilioError: undefined,
};

export const requestTwilioConfigThunk = createAsyncThunk('dialer/requestTwilioConfig', async () => {
    const response = await requestTwilioConfig();
    return response.data;
});

const dialerSlice = createSlice({
    name: 'dialer',
    initialState,
    reducers: {
        bootstrap(state) {
            state.isBootstrapped = true;
            //triggers middleware to request twilio config and get everything wired up
        },
        dispose(state) {
            state.isBootstrapped = false;
            state.twilioDevice = undefined;
            state.twilioCall = undefined;
            state.callStart = undefined;
            state.sendDigits = [];
        },
        callConnected(
            state,
            action: PayloadAction<{
                twilioDevice: SerializedTwilioDevice | undefined;
                twilioCall: SerializedTwilioCall | undefined;
                callStart: string | undefined;
            }>,
        ) {
            state.twilioDevice = action.payload.twilioDevice;
            state.twilioCall = action.payload.twilioCall;
            state.callStart = action.payload.callStart;
        },
        callRinging(
            state,
            action: PayloadAction<{
                twilioDevice: SerializedTwilioDevice | undefined;
                twilioCall: SerializedTwilioCall | undefined;
            }>,
        ) {
            state.twilioDevice = action.payload.twilioDevice;
            state.twilioCall = action.payload.twilioCall;
        },
        callDisconnected(state, action: PayloadAction<SerializedTwilioDevice | undefined>) {
            state.twilioDevice = action.payload;
            state.callStart = undefined;
            state.twilioCall = undefined;
        },
        deviceRegistered(state, action: PayloadAction<SerializedTwilioDevice | undefined>) {
            state.twilioDevice = action.payload;
        },
        deviceRegistering(state, action: PayloadAction<SerializedTwilioDevice | undefined>) {
            state.twilioDevice = action.payload;
        },
        deviceUnregistered(state, action: PayloadAction<SerializedTwilioDevice | undefined>) {
            state.twilioDevice = action.payload;
        },
        deviceError(state, action: PayloadAction<TwilioError>) {
            state.twilioError = action.payload;
            console.log('dialer TwilioError: ', action.payload);
        },
        deviceTokenWillExpire: state => {
            //handled by middleware
        },
        deviceChangeAudio: (state, action: PayloadAction<SerializedTwilioDevice | undefined>) => {
            state.twilioDevice = action.payload;
        },
        deviceIncoming: (
            state,
            action: PayloadAction<{
                twilioDevice: SerializedTwilioDevice | undefined;
                twilioCall: SerializedTwilioCall | undefined;
            }>,
        ) => {
            state.twilioDevice = action.payload.twilioDevice;
            state.twilioCall = action.payload.twilioCall;
        },
        startCall(
            state,
            action: PayloadAction<{
                phoneNumber: string;
                contactId: number;
                firstName: string;
                lastName: string;
            }>,
        ) {
            // handled by middleware
        },
        endCall(state) {
            state.callStart = undefined;
            state.twilioCall = undefined;
        },
        acceptCall(state) {
            // Logic for accepting call
        },
        destroyDevice(state) {
            state.twilioDevice = undefined;
        },
        sendDigits(state, action: PayloadAction<string>) {
            state.sendDigits.push(action.payload);
        },
        muteCall(state, action: PayloadAction<boolean>) {
            // handled by middleware
        },
    },
    extraReducers: builder => {
        builder.addCase(requestTwilioConfigThunk.fulfilled, (state, action) => {
            // handled by middleware
        });
        builder.addCase(requestTwilioConfigThunk.pending, state => {});
        builder.addCase(requestTwilioConfigThunk.rejected, state => {});
    },
});

export const {
    bootstrap,
    dispose,
    callConnected,
    callRinging,
    callDisconnected,
    deviceRegistered,
    deviceRegistering,
    deviceUnregistered,
    deviceTokenWillExpire,
    deviceChangeAudio,
    deviceError,
    deviceIncoming,
    startCall,
    endCall,
    acceptCall,
    sendDigits,
    muteCall,
} = dialerSlice.actions;

export default dialerSlice.reducer;

export const useDialerIsReady = createParametricSelectorHook(
    createSelector(
        [(state: RootState) => state.dialer],
        dialer =>
            dialer.twilioDevice?.state === 'registered' &&
            dialer.twilioDevice?.audioDevices.length > 0 &&
            !dialer?.twilioCall,
    ),
);

export const useDialerIsRegistered = createParametricSelectorHook(
    createSelector(
        [(state: RootState) => state.dialer],
        dialer =>
            dialer.twilioDevice?.state === 'registered' &&
            dialer.twilioDevice?.audioDevices.length > 0,
    ),
);
